import { readSearchSortParam, setSearchSortParam } from './searchSort'
import { getListQueryParamName, getListQueryParamValue } from './listQueryParams'

const SEARCH_DATA_FIELDS = [
  'searchPhrase',
  'doi',
  'issn',
  'isbn',
  'discipline',
  'field',
  'titleType',
  'languageOfPublication',
  'formOfPublication',
  'collection',
  'keywords',
  'type',
  'releases',
  'publisher',
  'publishingSeries',
  'authorsAffiliation',
  'orcid',
  'authorsCountry',
  'volume',
  'articleTitle',
  'chapterTitle',
  'monographTitle',
  'bookTitle',
  'bookType',
  'collectionName',
  'publicationTime',
  'publishedSince',
]

// Pola wspólne z listami mają w adresie zlokalizowane nazwy (np. `dziedzina` na polskiej stronie), tak jak
// tłumaczy je przełącznik języka. Pozostałe pola zachowują nazwy techniczne.
const LOCALIZED_FIELDS = ['field', 'discipline']

const getParamName = (name, locale) => (LOCALIZED_FIELDS.includes(name) && getListQueryParamName(name, locale)) || name

const getParamValue = (searchParams, name, locale) =>
  LOCALIZED_FIELDS.includes(name) ? getListQueryParamValue(searchParams, name, locale) : searchParams?.[name]

const getFirstValue = (value) => (Array.isArray(value) ? value[0] : value)

const normalizeAdvancedSearchPage = (value) => {
  const parsedValue = Number.parseInt(String(getFirstValue(value) || ''), 10)

  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 1
}

export const readAdvancedSearchPage = (searchParams, locale) =>
  normalizeAdvancedSearchPage(getListQueryParamValue(searchParams, 'page', locale))

export const hasAdvancedSearchParams = (searchParams = {}, locale) =>
  ['option', ...SEARCH_DATA_FIELDS, 'publicationDateFrom', 'publicationDateTo'].some((name) => {
    const value = getParamValue(searchParams, name, locale)

    return value !== undefined && value !== null
  }) || getListQueryParamValue(searchParams, 'sort', locale) !== undefined

export const serializeAdvancedSearchData = (data = {}, locale) => {
  const params = new URLSearchParams()
  params.set('option', String(data.option || 'page'))

  SEARCH_DATA_FIELDS.forEach((name) => {
    const value = data[name]

    if (value === undefined || value === null || value === '') {
      return
    }

    if (Array.isArray(value)) {
      value
        .filter((item) => item !== undefined && item !== null && item !== '')
        .forEach((item) => {
          params.append(getParamName(name, locale), String(item))
        })
      return
    }

    params.set(getParamName(name, locale), String(value))
  })

  const publicationDate = data.publicationDate

  if (publicationDate && typeof publicationDate === 'object') {
    if (publicationDate.from) {
      params.set('publicationDateFrom', String(publicationDate.from))
    }

    if (publicationDate.to) {
      params.set('publicationDateTo', String(publicationDate.to))
    }
  }

  setSearchSortParam(params, data.sortValue, locale)

  return params
}

export const parseAdvancedSearchData = (searchParams = {}, locale) => {
  const data = {
    option: String(getFirstValue(searchParams.option) || 'page'),
    sortValue: readSearchSortParam(searchParams, locale),
  }

  SEARCH_DATA_FIELDS.forEach((name) => {
    if (
      [
        'type',
        'discipline',
        'field',
        'publisher',
        'languageOfPublication',
        'formOfPublication',
        'authorsAffiliation',
        'publishingSeries',
        'collection',
        'collectionName',
      ].includes(name)
    ) {
      const rawValue = getParamValue(searchParams, name, locale)
      const values = Array.isArray(rawValue) ? rawValue : [rawValue]
      const normalizedValues = values
        .filter((value) => value !== undefined && value !== null && value !== '')
        .map((value) => String(value))

      if (normalizedValues.length > 0) {
        data[name] = name === 'type' || normalizedValues.length > 1 ? normalizedValues : normalizedValues[0]
      }

      return
    }

    const value = getFirstValue(getParamValue(searchParams, name, locale))

    if (value !== undefined && value !== null && value !== '') {
      data[name] = String(value)
    }
  })

  const publicationDateFrom = getFirstValue(searchParams.publicationDateFrom)
  const publicationDateTo = getFirstValue(searchParams.publicationDateTo)

  if (publicationDateFrom || publicationDateTo) {
    data.publicationDate = {
      from: String(publicationDateFrom || ''),
      to: String(publicationDateTo || ''),
    }
  }

  return data
}
